import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { money, fmtDate } from '../components/ui'
import { PAYMENT_TERMS, type Client, type Invoice, type InvoiceItem, type Project } from '../lib/db'

/** Vue imprimable d'une facture (admin et client). Ctrl+P / « Enregistrer en PDF ». */
export default function FacturePrint() {
  const { id } = useParams()
  const [inv, setInv] = useState<Invoice | null>(null)
  const [items, setItems] = useState<InvoiceItem[]>([])
  const [client, setClient] = useState<Client | null>(null)
  const [project, setProject] = useState<Project | null>(null)
  const [state, setState] = useState<'loading' | 'ok' | 'missing'>('loading')

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('invoices').select('*').eq('id', id).maybeSingle()
      if (!data) { setState('missing'); return }
      const i = data as Invoice; setInv(i)
      const [it, c, p] = await Promise.all([
        supabase.from('invoice_items').select('*').eq('invoice_id', i.id).order('sort_order'),
        supabase.from('clients').select('*').eq('id', i.client_id).maybeSingle(),
        supabase.from('projects').select('*').eq('id', i.project_id).maybeSingle(),
      ])
      setItems((it.data as any) || []); setClient(c.data as any); setProject(p.data as any)
      document.title = `Facture ${i.invoice_number || ''} · Pivot Média`
      setState('ok')
    })()
  }, [id])

  if (state === 'loading') return <div className="min-h-screen bg-white text-black p-10">Chargement…</div>
  if (state === 'missing' || !inv) return <div className="min-h-screen bg-white text-black p-10">Facture introuvable ou accès refusé. <a href="/login" className="underline">Se connecter</a></div>
  const terms = PAYMENT_TERMS.find(t => t[0] === inv.payment_terms)?.[1] || 'Payable à réception'

  return (
    <div className="min-h-screen bg-white text-[#111]" style={{ fontFamily: '"DM Sans", Helvetica, Arial, sans-serif' }}>
      <style>{`@media print { .no-print { display: none !important } body { background: #fff } @page { margin: 14mm } }`}</style>
      <div className="no-print bg-[#111] text-white px-6 py-3 flex items-center justify-between text-sm">
        <span>Aperçu de la facture</span>
        <div className="flex gap-3">
          <button onClick={() => window.print()} className="bg-[#C05A1F] px-4 py-2 rounded-sm font-bold uppercase tracking-widest text-xs">🖨 Imprimer / Enregistrer en PDF</button>
          <button onClick={() => window.close()} className="border border-white/30 px-4 py-2 rounded-sm uppercase tracking-widest text-xs">Fermer</button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-10">
        <div className="flex justify-between items-start border-b-4 border-[#C05A1F] pb-6">
          <div>
            <div style={{ fontFamily: '"Bebas Neue", sans-serif' }} className="text-3xl tracking-[0.25em]"><span className="text-[#C05A1F]">[</span> PIVOT <span className="text-[#C05A1F]">]</span> <span className="text-sm tracking-[0.4em] text-[#8A8A8A]">MÉDIA</span></div>
            <div className="text-xs text-[#555] mt-2 leading-relaxed">Pivot Média Inc.<br />Production vidéo industrielle · Québec<br />info@pivot-media.ca · pivot-media.ca</div>
          </div>
          <div className="text-right">
            <div style={{ fontFamily: '"Bebas Neue", sans-serif' }} className="text-4xl tracking-wider">FACTURE</div>
            <div className="text-sm mt-1"><b>{inv.invoice_number || '—'}</b></div>
            <div className="text-xs text-[#555] mt-2">Date : {fmtDate(inv.created_at)}<br />Échéance : {fmtDate(inv.due_date)}<br />{terms}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mt-8 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C05A1F] mb-1">Facturé à</div>
            <div className="font-bold">{client?.company || client?.full_name}</div>
            {client?.company && <div>{client.full_name}</div>}
            <div className="text-[#555]">{[client?.address, [client?.city, client?.province, client?.postal_code].filter(Boolean).join(' ')].filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}{client?.email}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C05A1F] mb-1">Projet</div>
            <div className="font-bold">{project?.title || '—'}</div>
            {project?.description && <div className="text-[#555]">{project.description}</div>}
          </div>
        </div>

        <table className="w-full mt-8 text-sm">
          <thead><tr className="border-b-2 border-[#111] text-[10px] uppercase tracking-[0.2em] text-[#555]"><th className="text-left py-2">Description</th><th className="text-center py-2 w-16">Qté</th><th className="text-right py-2 w-28">Prix unitaire</th><th className="text-right py-2 w-28">Montant</th></tr></thead>
          <tbody>{items.map((it, i) => <tr key={i} className="border-b border-[#ddd]"><td className="py-2 pr-3">{it.description}</td><td className="py-2 text-center">{it.quantity}</td><td className="py-2 text-right">{money(it.unit_price)}</td><td className="py-2 text-right">{money(it.amount)}</td></tr>)}</tbody>
        </table>

        <div className="flex justify-end mt-4 text-sm">
          <div className="w-64 space-y-1">
            <div className="flex justify-between"><span className="text-[#555]">Sous-total</span><span>{money(inv.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-[#555]">TPS (5 %)</span><span>{money(inv.tps_amount)}</span></div>
            <div className="flex justify-between"><span className="text-[#555]">TVQ (9,975 %)</span><span>{money(inv.tvq_amount)}</span></div>
            <div className="flex justify-between border-t-2 border-[#111] pt-2 mt-2 text-lg font-bold"><span>Total</span><span className="text-[#C05A1F]">{money(inv.amount)}</span></div>
            {inv.status === 'payée' && <div className="text-right text-xs text-green-700 font-bold uppercase tracking-widest pt-1">Payée le {fmtDate(inv.paid_date)}</div>}
          </div>
        </div>

        {inv.notes && <div className="mt-8 text-sm"><div className="text-[10px] uppercase tracking-[0.3em] text-[#C05A1F] mb-1">Notes</div><p className="whitespace-pre-line text-[#333]">{inv.notes}</p></div>}
        <div className="mt-12 text-xs text-[#888] border-t border-[#ddd] pt-4">Merci de votre confiance. Pour toute question : info@pivot-media.ca</div>
      </div>
    </div>
  )
}
